import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  Rocket,
  Server,
  Layers,
  AlertTriangle,
  HardDrive,
  Scan,
  Pencil,
  Trash2,
  Loader2,
  FolderTree,
  ChevronDown,
  ChevronUp,
  Route,
} from "lucide-react";
import { groupsApi } from "@/api/client";
import { formatRelativeTime, titleCase } from "@/lib/utils";
import { useState, useMemo } from "react";
import SDLCPathEditor from "@/components/SDLCPathEditor";

export default function GroupDashboard() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSDLCPath, setShowSDLCPath] = useState(false);

  const { data: stats, isLoading } = useQuery({
    queryKey: ["group-stats", id],
    queryFn: () => groupsApi.getStats(id!),
    enabled: !!id,
  });

  const { data: sdlcPath, refetch: refetchSDLCPath } = useQuery({
    queryKey: ["sdlc-path", id],
    queryFn: () => groupsApi.getSDLCPath(id!),
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: groupsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      navigate("/groups");
    },
  });

  const handleDelete = () => {
    if (id) {
      deleteMutation.mutate(id);
    }
  };

  // Sort environments by SDLC path order (reverse: PROD first, then UAT, etc.)
  // Off-path environments go at the end
  // Note: Must be before early returns to satisfy React hooks rules
  const sortedEnvironmentStats = useMemo(() => {
    const envStats = stats?.environment_stats;
    if (!envStats || envStats.length === 0) {
      return [];
    }

    // If no SDLC path is configured, return as-is
    const inPath = sdlcPath?.in_path;
    if (!inPath || inPath.length === 0) {
      return envStats;
    }

    // Create a map of environment ID to its SDLC order (reversed so PROD is first)
    const sdlcOrderMap = new Map<string, number>();
    const reversedPath = [...inPath].reverse();
    reversedPath.forEach((env, index) => {
      sdlcOrderMap.set(env.id, index);
    });

    // Sort: in-path envs first (by reverse SDLC order), then off-path envs
    return [...envStats].sort((a, b) => {
      const aOrder = sdlcOrderMap.get(a.id);
      const bOrder = sdlcOrderMap.get(b.id);

      // Both in path - sort by SDLC order
      if (aOrder !== undefined && bOrder !== undefined) {
        return aOrder - bOrder;
      }
      // Only a is in path - a comes first
      if (aOrder !== undefined) return -1;
      // Only b is in path - b comes first
      if (bOrder !== undefined) return 1;
      // Neither in path - sort alphabetically
      return a.name.localeCompare(b.name);
    });
  }, [stats?.environment_stats, sdlcPath?.in_path]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-12">
        <FolderTree className="h-12 w-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">Group not found</p>
        <Link
          to="/groups"
          className="text-blue-600 hover:underline mt-2 inline-block"
        >
          Back to Groups
        </Link>
      </div>
    );
  }

  const { group } = stats;

  const statCards = [
    {
      name: "Environments",
      value: stats.total_environments,
      icon: Server,
      color: "text-purple-600 bg-purple-100",
      link: `/environments?group=${group.slug}`,
    },
    {
      name: "Services",
      value: stats.total_services,
      icon: Layers,
      color: "text-green-600 bg-green-100",
      link: `/services?group=${group.slug}`,
    },
    {
      name: "Servers",
      value: stats.total_servers,
      icon: HardDrive,
      color: "text-indigo-600 bg-indigo-100",
      link: `/servers?group=${group.slug}`,
    },
    {
      name: "Drift Detected",
      value: stats.unresolved_drift,
      icon: AlertTriangle,
      color:
        stats.unresolved_drift > 0
          ? "text-red-600 bg-red-100"
          : "text-green-600 bg-green-100",
      link: `/drift-events?group=${group.slug}`,
    },
    {
      name: "Total Deployments",
      value: stats.total_deployments,
      icon: Rocket,
      color: "text-blue-600 bg-blue-100",
      link: `/deployments?group=${group.slug}`,
    },
    {
      name: "Recent Scans",
      value: stats.recent_scans,
      icon: Scan,
      color: "text-cyan-600 bg-cyan-100",
      link: `/scans?group=${group.slug}`,
    },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <Link
            to="/groups"
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-2"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Groups
          </Link>
          <div className="flex items-center gap-3">
            <FolderTree className="h-8 w-8 text-brand-500" />
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">
                {group.name}
              </h1>
              <p className="text-sm text-gray-500">{group.slug}</p>
            </div>
          </div>
          {group.description && (
            <p className="mt-2 text-gray-600">{group.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/groups?edit=${group.id}`)}
            className="btn btn-secondary flex items-center gap-2"
          >
            <Pencil className="h-4 w-4" />
            Edit
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="btn btn-secondary text-red-600 hover:text-red-700 flex items-center gap-2"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Delete Group
            </h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete "{group.name}"? Environments and
              services in this group will become ungrouped.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="btn bg-red-600 text-white hover:bg-red-700"
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Delete"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {statCards.map((stat) => (
          <Link
            key={stat.name}
            to={stat.link}
            className="card hover:shadow-md transition-shadow"
          >
            <div className="flex items-center">
              <div className={`rounded-md p-3 ${stat.color}`}>
                <stat.icon className="h-6 w-6" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">{stat.name}</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {stat.value}
                </p>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* SDLC Path Configuration */}
      <div className="mt-8">
        <div className="card">
          <button
            onClick={() => setShowSDLCPath(!showSDLCPath)}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Route className="h-5 w-5 text-purple-600" />
              </div>
              <div className="text-left">
                <h3 className="font-medium text-gray-900">
                  SDLC Promotion Path
                </h3>
                <p className="text-sm text-gray-500">
                  {sdlcPath?.in_path && sdlcPath.in_path.length > 0
                    ? `${sdlcPath.in_path.length} environments in path: ${sdlcPath.in_path.map((e) => e.name).join(" → ")}`
                    : "Configure the deployment promotion order"}
                </p>
              </div>
            </div>
            {showSDLCPath ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </button>

          {showSDLCPath && sdlcPath && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <SDLCPathEditor
                groupId={group.id}
                groupName={group.name}
                sdlcPath={sdlcPath}
                onUpdate={() => refetchSDLCPath()}
              />
            </div>
          )}
        </div>
      </div>

      {/* Environment Status */}
      <div className="mt-8">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium text-gray-900">
              Environment Status
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Environments in this group with drift detection status
            </p>
          </div>
        </div>

        <div className="mt-4 grid gap-6 lg:grid-cols-2">
          {sortedEnvironmentStats.length === 0 ? (
            <div className="card col-span-2 text-center py-8">
              <Server className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No environments
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                This group doesn't have any environments yet.
              </p>
              <Link
                to={`/environments?group=${group.slug}`}
                className="mt-4 inline-block text-blue-600 hover:underline"
              >
                Add an environment
              </Link>
            </div>
          ) : (
            sortedEnvironmentStats.map((env) => (
              <div
                key={env.id}
                className={`card ${env.has_drift ? "border-l-4 border-l-red-500" : ""}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-gray-900">{env.name}</h3>
                    {env.is_production && (
                      <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                        Production
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {env.has_drift ? (
                      <Link
                        to={`/drift-events?environment=${env.id}`}
                        className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800 hover:bg-red-200"
                      >
                        <AlertTriangle className="h-3 w-3" />
                        {env.drift_count} drift
                        {env.drift_count !== 1 ? "s" : ""}
                      </Link>
                    ) : env.last_scan_at ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                        In sync
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                        Not scanned
                      </span>
                    )}
                  </div>
                </div>

                {/* Services List */}
                {(env.services ?? []).length === 0 ? (
                  <p className="text-sm text-gray-500">No services deployed</p>
                ) : (
                  <ul className="space-y-3">
                    {env.services.map((svc) => (
                      <li
                        key={svc.service_id}
                        className="flex items-center justify-between text-sm"
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">
                            {titleCase(svc.service_name)}
                          </span>
                          {svc.is_primary && (
                            <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                              Primary
                            </span>
                          )}
                          <span className="text-gray-500">
                            {svc.version === "initial"
                              ? svc.version
                              : `v${svc.version}`}
                          </span>
                          {svc.has_drift && (
                            <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
                              Drift Detected
                            </span>
                          )}
                          {svc.previous_version &&
                            svc.previous_version !== svc.version &&
                            svc.previous_version !== "initial" && (
                              <span className="text-gray-400">
                                ← v{svc.previous_version}
                              </span>
                            )}
                        </div>
                        <span className="text-gray-400">
                          {formatRelativeTime(svc.deployed_at)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Server count */}
                {env.server_count > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
                    {env.server_count} server
                    {env.server_count !== 1 ? "s" : ""} monitored
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Recent Deployments */}
      <div className="mt-8">
        <h2 className="text-lg font-medium text-gray-900">
          Recent Deployments
        </h2>

        <div className="mt-4 card overflow-hidden p-0">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Service
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Version
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Environment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Deployed By
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  When
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(stats.recent_deployments ?? []).map((deployment) => (
                <tr key={deployment.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {titleCase(deployment.service_name)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {deployment.version}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {deployment.environment_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {deployment.deployed_by}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatRelativeTime(deployment.deployed_at)}
                  </td>
                </tr>
              ))}

              {(stats.recent_deployments ?? []).length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-8 text-center text-sm text-gray-500"
                  >
                    No deployments recorded for this group yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
